import { required, useTranslate } from "ra-core";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import type { Sale } from "../types";
import { dayChoices, genderChoices } from "./choices";
import { PriceInput } from "./PriceInput";

// TextInput (not NumberInput) so a cleared field saves null rather than 0:
// an empty capacity means "no cap", not "full".

const coachOptionText = (coach: Sale) =>
  `${coach.first_name} ${coach.last_name}`;

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4 flex-1">
      <h3 className="text-base font-medium">{translate(title)}</h3>
      {children}
    </div>
  );
};

export const ClassInputs = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-8 p-1">
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <Section title="resources.classes.field_categories.details">
          <ReferenceInput source="program_id" reference="programs">
            <SelectInput
              optionText="name"
              validate={required()}
              helperText={false}
            />
          </ReferenceInput>
          <TextInput source="name" validate={required()} helperText={false} />
          <ReferenceInput source="coach_id" reference="sales">
            <SelectInput optionText={coachOptionText} helperText={false} />
          </ReferenceInput>
          <PriceInput source="price_cents" helperText={false} />
          <BooleanInput source="active" />
        </Section>
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <Section title="resources.classes.field_categories.schedule">
          <TextInput source="location" helperText={false} />
          <SelectInput
            source="day_of_week"
            choices={dayChoices}
            helperText={false}
          />
          <div className="flex gap-4">
            <TextInput
              source="start_time"
              type="time"
              className="flex-1"
              helperText={false}
            />
            <TextInput
              source="end_time"
              type="time"
              className="flex-1"
              helperText={false}
            />
          </div>
        </Section>
      </div>
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <Section title="resources.classes.field_categories.audience">
          <SelectInput
            source="gender"
            choices={genderChoices}
            helperText={false}
          />
          <div className="flex gap-4">
            <TextInput
              type="number"
              source="age_min"
              min={0}
              className="flex-1"
              helperText={false}
            />
            <TextInput
              type="number"
              source="age_max"
              min={0}
              className="flex-1"
              helperText={false}
            />
          </div>
          <TextInput
            type="number"
            source="capacity"
            min={0}
            helperText={false}
          />
        </Section>
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <Section title="resources.classes.field_categories.acuity">
          <p className="text-sm text-muted-foreground">
            {translate("resources.classes.acuity_helper")}
          </p>
          <TextInput
            type="number"
            source="acuity_appointment_type_id"
            helperText={false}
          />
          <TextInput
            type="number"
            source="acuity_calendar_id"
            helperText={false}
          />
        </Section>
      </div>
    </div>
  );
};
